import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Check, X, Users, UserPlus, CalendarClock, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState, KpiCard, StatusBadge } from "@/command/ccUi";

const STAGES = ["Onboarding", "Active", "On Leave", "Notice", "Exited"];
const EMP_FIELDS = [
  ["name", "Full name"], ["email", "Email"], ["title", "Title"], ["department", "Department"],
  ["location", "Location"], ["employment_type", "Employment type"], ["join_date", "Join date"],
];

function EmployeesTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/people/employees").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["people"], load);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/cc/people/employees/${editing.id}`, editing);
      else await api.post("/cc/people/employees", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Remove this employee?")) return; await api.delete(`/cc/people/employees/${id}`); toast.success("Removed"); load(); };

  return (
    <Panel testid="employees-panel"
      action={canEdit && <CCButton onClick={() => setEditing({ status: "Onboarding" })} testid="employee-add"><Plus className="w-4 h-4" /> Add employee</CCButton>}>
      {loading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState title="No employees yet" hint={canEdit ? "Add your first team member." : undefined} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
              <th className="px-3 py-3 font-medium">Name</th><th className="px-3 py-3 font-medium">Title</th>
              <th className="px-3 py-3 font-medium">Department</th><th className="px-3 py-3 font-medium">Stage</th>
              <th className="px-3 py-3 font-medium">Location</th>
              {canEdit && <th className="px-3 py-3 text-right">Actions</th>}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-hairline/[0.05] text-ink" data-testid={`employee-row-${r.id}`}>
                  <td className="px-3 py-3"><div>{r.name}</div><div className="text-[11px] text-subtle">{r.email}</div></td>
                  <td className="px-3 py-3">{r.title || "—"}</td>
                  <td className="px-3 py-3">{r.department || "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={r.status === "Active" ? "healthy" : r.status === "Exited" ? "critical" : "pending"} label={r.status} /></td>
                  <td className="px-3 py-3">{r.location || "—"}</td>
                  {canEdit && <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing({ ...r })} className="text-subtle hover:text-ink p-1.5" data-testid={`employee-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(r.id)} className="text-subtle hover:text-red-500 p-1.5" data-testid={`employee-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditing(null)} data-testid="employee-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">{editing.id ? "Edit" : "Add"} employee</h3>
            <div className="grid grid-cols-2 gap-4">
              {EMP_FIELDS.map(([f, l]) => (
                <Field key={f} label={l}><input className={inputCls} value={editing[f] || ""} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} data-testid={`employee-field-${f}`} /></Field>
              ))}
              <Field label="Lifecycle stage">
                <select className={inputCls} value={editing.status || "Onboarding"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="employee-field-status">
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={save} testid="employee-save">Save</CCButton>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

function LeaveTab({ canCreate, canApprove }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/people/leave").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["people"], load);

  const decide = async (id, decision) => {
    try { await api.post(`/cc/people/leave/${id}/decision`, { decision }); toast.success(`Leave ${decision}d`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    try { await api.post("/cc/people/leave", adding); toast.success("Leave requested"); setAdding(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel testid="leave-panel"
      action={canCreate && <CCButton onClick={() => setAdding({ type: "Casual", status: "Pending" })} testid="leave-add"><Plus className="w-4 h-4" /> Request leave</CCButton>}>
      {loading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState title="No leave requests yet" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
              <th className="px-3 py-3 font-medium">Employee</th><th className="px-3 py-3 font-medium">Type</th>
              <th className="px-3 py-3 font-medium">From</th><th className="px-3 py-3 font-medium">To</th>
              <th className="px-3 py-3 font-medium">Days</th><th className="px-3 py-3 font-medium">Status</th>
              {canApprove && <th className="px-3 py-3 text-right">Decision</th>}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-hairline/[0.05] text-ink" data-testid={`leave-row-${r.id}`}>
                  <td className="px-3 py-3">{r.employee_name}</td>
                  <td className="px-3 py-3">{r.type}</td>
                  <td className="px-3 py-3">{r.from_date}</td>
                  <td className="px-3 py-3">{r.to_date}</td>
                  <td className="px-3 py-3">{r.days}</td>
                  <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
                  {canApprove && <td className="px-3 py-3 text-right whitespace-nowrap">
                    {r.status === "Pending" ? (<>
                      <button onClick={() => decide(r.id, "approve")} className="text-subtle hover:text-emerald-500 p-1.5" data-testid={`leave-approve-${r.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => decide(r.id, "reject")} className="text-subtle hover:text-red-500 p-1.5" data-testid={`leave-reject-${r.id}`}><X className="w-4 h-4" /></button>
                    </>) : <span className="text-subtle text-xs">{r.decided_by || "—"}</span>}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setAdding(null)} data-testid="leave-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">Request leave</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Employee name"><input className={inputCls} value={adding.employee_name || ""} onChange={(e) => setAdding({ ...adding, employee_name: e.target.value })} data-testid="leave-field-name" /></Field>
              <Field label="Type">
                <select className={inputCls} value={adding.type} onChange={(e) => setAdding({ ...adding, type: e.target.value })} data-testid="leave-field-type">
                  {["Casual", "Sick", "Earned", "Unpaid"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="From"><input type="date" className={inputCls} value={adding.from_date || ""} onChange={(e) => setAdding({ ...adding, from_date: e.target.value })} data-testid="leave-field-from" /></Field>
              <Field label="To"><input type="date" className={inputCls} value={adding.to_date || ""} onChange={(e) => setAdding({ ...adding, to_date: e.target.value })} data-testid="leave-field-to" /></Field>
              <Field label="Days"><input type="number" className={inputCls} value={adding.days || ""} onChange={(e) => setAdding({ ...adding, days: Number(e.target.value) })} data-testid="leave-field-days" /></Field>
              <Field label="Reason"><input className={inputCls} value={adding.reason || ""} onChange={(e) => setAdding({ ...adding, reason: e.target.value })} data-testid="leave-field-reason" /></Field>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setAdding(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={save} testid="leave-save">Submit</CCButton>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

function RecruitmentTab({ summary }) {
  const [openings, setOpenings] = useState([]);
  useEffect(() => { api.get("/careers").then(({ data }) => setOpenings(data)).catch(() => {}); }, []);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Panel title="Open positions" testid="recruitment-panel">
          {openings.length === 0 ? <EmptyState title="No open positions" hint="Roles are managed in the website CMS." /> : (
            <div className="flex flex-col gap-3">
              {openings.map((o) => (
                <div key={o.id} className="p-4 rounded-xl glass-medium flex items-center justify-between" data-testid={`opening-${o.id}`}>
                  <div><p className="text-ink font-medium">{o.title}</p><p className="text-xs text-subtle mt-0.5">{o.department} · {o.location} · {o.employment_type}</p></div>
                  <StatusBadge status="healthy" label={o.status || "open"} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
      <Panel title="Pipeline" testid="recruitment-stats">
        <div className="flex flex-col gap-4">
          <div><p className="text-xs uppercase tracking-[0.16em] text-subtle">New applications</p><p className="display text-3xl font-light text-ink mt-1">{summary?.applications_new ?? 0}</p></div>
          <div><p className="text-xs uppercase tracking-[0.16em] text-subtle">Total applications</p><p className="display text-3xl font-light text-ink mt-1">{summary?.applications_total ?? 0}</p></div>
          <p className="text-xs text-subtle">Applications are reviewed in the website CMS admin.</p>
        </div>
      </Panel>
    </div>
  );
}

export default function People() {
  const { can } = useCommand();
  const [tab, setTab] = useState("employees");
  const [summary, setSummary] = useState(null);
  const canCreate = can("people", "CREATE") || can("people", "MANAGE");
  const canEdit = can("people", "EDIT") || can("people", "MANAGE");
  const canApprove = can("people", "APPROVE") || can("people", "MANAGE");

  const load = useCallback(() => { api.get("/cc/people/summary").then(({ data }) => setSummary(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["people"], load);

  const TABS = { employees: "Employees", leave: "Leave", recruitment: "Recruitment" };

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">People &amp; HR</h1>
      <p className="text-subtle mt-1 mb-6">Employees, lifecycle, recruitment and leave — live.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Headcount" value={summary?.headcount ?? 0} />
        <KpiCard label="Open Positions" value={summary?.open_positions ?? 0} />
        <KpiCard label="Pending Leave" value={summary?.leave_pending ?? 0} />
        <KpiCard label="New Applications" value={summary?.applications_new ?? 0} />
      </div>

      <Panel title="Lifecycle" testid="lifecycle-panel" className="mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {(summary?.stages || STAGES.map((s) => ({ stage: s, count: 0 }))).map((s) => (
            <div key={s.stage} className="text-center glass-medium rounded-xl py-4" data-testid={`stage-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="display text-2xl font-light text-ink">{s.count}</p>
              <p className="text-xs text-subtle mt-1">{s.stage}</p>
            </div>
          ))}
        </div>
      </Panel>

      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(TABS).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`people-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === k ? "bg-invert text-invert-ink" : "glass-medium text-subtle hover:text-ink"}`}>{v}</button>
        ))}
      </div>

      {tab === "employees" && <EmployeesTab canEdit={canCreate || canEdit} />}
      {tab === "leave" && <LeaveTab canCreate={canCreate} canApprove={canApprove} />}
      {tab === "recruitment" && <RecruitmentTab summary={summary} />}
    </div>
  );
}
